import React, { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { sessionAtom } from '../../../jotai/atoms/session';
import { calculateHash } from '../../../utils/helper';
import { ContentPanel } from '../../UI/ContentPanel';
import { SidePanel } from '../../UI/SidePanel';
import { Grid } from './Grid';
import { Clues } from './Clues';
import { printCrossword } from '../../../utils/pdf-export';
const Crossword = ({ code, data, onSubmit }) => {
    const [session, setSession] = useAtom(sessionAtom);
    const [grid, setGrid] = useState(null);
    const [dimensions, setDimensions] = useState({ length: 0, height: 0 });
    const [startPositions, setStartPositions] = useState({
        vertical: null,
        horizontal: null,
    });
    const [direction, setDirection] = useState('across');
    const [attemptedWords, setAttemptedWords] = useState([]);
    useEffect(() => {
        setSession((session) => ({
            ...session,
            duration: Number(data.duration),
        }));
        const verticalStart = data.hints.down.reduce((prev, curr) => prev.start[0] < curr.start[0] ? prev : curr);
        const verticalEnd = data.hints.down.reduce((prev, curr) => prev.start[0] + prev.length > curr.start[0] + curr.length ? prev : curr);
        const horizontalStart = data.hints.across.reduce((prev, curr) => (prev.start[1] < curr.start[1] ? prev : curr));
        const horizontalEnd = data.hints.across.reduce((prev, curr) => prev.start[1] + prev.length > curr.start[1] + curr.length ? prev : curr);
        const length = horizontalEnd.start[1] + horizontalEnd.length - horizontalStart.start[1];
        const height = verticalEnd.start[0] + verticalEnd.length - verticalStart.start[0];
        setStartPositions({ vertical: verticalStart, horizontal: horizontalStart });
        setDimensions({ length, height });
        // const grid = Array(data.gridSize[1])
        //   .fill()
        //   .map(() => Array(data.gridSize[0]).fill({ type: 'blank' }));
        const newGrid = Array(height)
            .fill(null)
            .map(() => Array(length).fill({ type: 'blank' }));
        data.hints.across.forEach((hint, _k) => {
            newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]] = {
                type: 'word',
                hintNumber: hint.number,
                hints: [{ ...hint, direction: 'across' }],
            };
            for (let i = 1; i < hint.length; i++) {
                newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1] + i] = {
                    type: 'word',
                    hints: [{ ...hint, direction: 'across' }],
                };
            }
        });
        data.hints.down.forEach((hint, _k) => {
            if (newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]].type !== 'word') {
                newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]] = {
                    type: 'word',
                    hintNumber: hint.number,
                    hints: [{ ...hint, direction: 'down' }],
                };
            }
            else {
                newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]] = {
                    ...newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]],
                    hintNumber: hint.number,
                    hints: [
                        ...(newGrid[hint.start[0] - verticalStart.start[0]][hint.start[1] - horizontalStart.start[1]].hints || []),
                        { ...hint, direction: 'down' },
                    ],
                    intersection: true,
                };
            }
            for (let i = 1; i < hint.length; i++) {
                if (newGrid[hint.start[0] - verticalStart.start[0] + i][hint.start[1] - horizontalStart.start[1]].type !== 'word') {
                    newGrid[hint.start[0] - verticalStart.start[0] + i][hint.start[1] - horizontalStart.start[1]] = {
                        type: 'word',
                        hints: [{ ...hint, direction: 'down' }],
                    };
                }
                else {
                    newGrid[hint.start[0] - verticalStart.start[0] + i][hint.start[1] - horizontalStart.start[1]] = {
                        ...newGrid[hint.start[0] - verticalStart.start[0] + i][hint.start[1] - horizontalStart.start[1]],
                        hints: [
                            ...(newGrid[hint.start[0] - verticalStart.start[0] + i][hint.start[1] - horizontalStart.start[1]].hints || []),
                            { ...hint, direction: 'down' },
                        ],
                        intersection: true,
                    };
                }
            }
        });
        setGrid(newGrid);
    }, [data]);
    const handleFocus = (e, cell) => {
        e.target.select();
        const id = e.target.id.split('-');
        if (cell.intersection) {
            if (direction === 'down') {
                const oneUp = document.getElementById(`${Number(id[0]) - 1}-${Number(id[1])}`);
                if (oneUp && /^[a-zA-Z]$/.test(oneUp.value)) {
                    setDirection(cell.hints?.[1]?.direction || 'across');
                }
                else {
                    setDirection(cell.hints?.[0]?.direction || 'across');
                }
            }
        }
        else {
            setDirection(cell.hints?.[0]?.direction || 'across');
        }
        cell.hints?.forEach((hint) => {
            const clues = document.getElementsByClassName(`clue-${hint.number}-${hint.direction}`);
            for (const clue of clues) {
                // clue.style.fontWeight = 'bold';
                clue.style.color = '#7400a0';
                clue.style.backgroundColor = 'lightgray';
            }
        });
    };
    const handleBlur = (e, cell) => {
        cell.hints?.forEach((hint) => {
            const clues = document.getElementsByClassName(`clue-${hint.number}-${hint.direction}`);
            for (const clue of clues) {
                // clue.style.fontWeight = 'normal';
                clue.style.color = 'black';
                clue.style.backgroundColor = 'white';
            }
        });
    };
    const handleKeyUp = async (e, cell) => {
        let nextCell;
        const id = e.target.id.split('-');
        if (!/^[a-zA-Z]$/.test(e.key)) {
            switch (e.key) {
                case 'ArrowRight':
                    nextCell = document.getElementById(`${id[0]}-${Number(id[1]) + 1}`);
                    break;
                case 'ArrowDown':
                    nextCell = document.getElementById(`${Number(id[0]) + 1}-${id[1]}`);
                    break;
                case 'ArrowLeft':
                    nextCell = document.getElementById(`${id[0]}-${Number(id[1]) - 1}`);
                    break;
                case 'ArrowUp':
                    nextCell = document.getElementById(`${Number(id[0]) - 1}-${id[1]}`);
                    break;
                default:
            }
        }
        if (nextCell) {
            nextCell.select();
            nextCell.focus();
        }
    };
    const handleChange = async (e, cell) => {
        let nextCell;
        const id = e.target.id.split('-');
        if (direction === 'across') {
            nextCell = document.getElementById(`${id[0]}-${Number(id[1]) + 1}`);
        }
        else {
            nextCell = document.getElementById(`${Number(id[0]) + 1}-${id[1]}`);
        }
        await checkAnswer(cell);
        if (nextCell) {
            nextCell.select();
            nextCell.focus();
        }
    };
    // const handleToPDF = () => {
    //   exportToPDF('crossword-grid-container', 'crossword-puzzle.pdf');
    // };
    const handlePrint = () => {
        printCrossword('crossword-grid', dimensions.length, dimensions.height, data.name, session.user?.name);
    };
    async function checkAnswer(cell) {
        cell.hints?.forEach(async (hint) => {
            if (hint.direction === 'across') {
                let word = '';
                for (let i = 0; i < hint.length; i++) {
                    word =
                        word +
                            document.getElementById(`${hint.start[0] - startPositions.vertical.start[0]}-${hint.start[1] - startPositions.horizontal.start[1] + i}`)?.value.toUpperCase() || '';
                }
                if (word.length === hint.length) {
                    const w = attemptedWords.find((aw) => aw.number === hint.number && aw.direction === hint.direction);
                    if (w) {
                        setAttemptedWords([
                            ...attemptedWords.filter((aw) => aw.number !== hint.number && aw.direction !== hint.direction),
                            {
                                number: hint.number,
                                direction: hint.direction || 'across',
                                word,
                                clue: hint.clue,
                            },
                        ]);
                    }
                    else {
                        attemptedWords.push({
                            number: hint.number,
                            direction: hint.direction || 'across',
                            word,
                            clue: hint.clue,
                        });
                    }
                    const hash = await calculateHash(word);
                    if (hash === hint.hash) {
                        for (let i = 0; i < hint.length; i++) {
                            const input = document.getElementById(`${hint.start[0] - startPositions.vertical.start[0]}-${hint.start[1] - startPositions.horizontal.start[1] + i}`);
                            if (input) {
                                input.style.color = 'green';
                                input.style.fontWeight = 'bold';
                            }
                        }
                    }
                    else {
                        for (let i = 0; i < hint.length; i++) {
                            const input = document.getElementById(`${hint.start[0] - startPositions.vertical.start[0]}-${hint.start[1] - startPositions.horizontal.start[1] + i}`);
                            if (input) {
                                input.style.color = 'red';
                                input.style.fontWeight = 'bold';
                            }
                        }
                    }
                }
                else {
                    for (let i = 0; i < hint.length; i++) {
                        const input = document.getElementById(`${hint.start[0] - startPositions.vertical.start[0]}-${hint.start[1] - startPositions.horizontal.start[1] + i}`);
                        if (input) {
                            input.style.color = '#7400a0';
                        }
                    }
                }
            }
            else {
                let word = '';
                for (let i = 0; i < hint.length; i++) {
                    word =
                        word +
                            document.getElementById(`${hint.start[0] - startPositions.vertical.start[0] + i}-${hint.start[1] - startPositions.horizontal.start[1]}`)?.value.toUpperCase() || '';
                }
                if (word.length === hint.length) {
                    const w = attemptedWords.find((aw) => aw.number === hint.number && aw.direction === hint.direction);
                    if (w) {
                        setAttemptedWords([
                            ...attemptedWords.filter((aw) => aw.number !== hint.number && aw.direction !== hint.direction),
                            {
                                number: hint.number,
                                direction: hint.direction || 'down',
                                word,
                                clue: hint.clue,
                            },
                        ]);
                    }
                    else {
                        attemptedWords.push({
                            number: hint.number,
                            direction: hint.direction || 'down',
                            word,
                            clue: hint.clue,
                        });
                    }
                    const hash = await calculateHash(word);
                    if (hash === hint.hash) {
                        for (let i = 0; i < hint.length; i++) {
                            const input = document.getElementById(`${hint.start[0] - startPositions.vertical.start[0] + i}-${hint.start[1] - startPositions.horizontal.start[1]}`);
                            if (input) {
                                input.style.color = 'green';
                                input.style.fontWeight = 'bold';
                            }
                        }
                    }
                    else {
                        for (let i = 0; i < hint.length; i++) {
                            const input = document.getElementById(`${hint.start[0] - startPositions.vertical.start[0] + i}-${hint.start[1] - startPositions.horizontal.start[1]}`);
                            if (input) {
                                input.style.color = 'red';
                                input.style.fontWeight = 'bold';
                            }
                        }
                    }
                }
                else {
                    for (let i = 0; i < hint.length; i++) {
                        const input = document.getElementById(`${hint.start[0] - startPositions.vertical.start[0] + i}-${hint.start[1] - startPositions.horizontal.start[1]}`);
                        if (input) {
                            input.style.color = '#7400a0';
                        }
                    }
                }
            }
        });
    }
    if (!grid) {
        return React.createElement("div", null, "Loading..."); // Add a loading state while grid is being created
    }
    const handleSubmitQuiz = () => {
        onSubmit(code, {
            fullName: session.user.name,
            email: session.user.email,
            attemptedWords: attemptedWords.map((word) => ({
                clue: word.clue,
                word: word.word,
            })),
        });
        setSession((session) => ({
            ...session,
            submitted: true,
            duration: 0,
        }));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(ContentPanel, { component: Grid, props: {
                grid,
                length: dimensions.length,
                height: dimensions.height,
                handleFocus,
                handleBlur,
                handleKeyUp,
                handleChange,
                // handleToPDF,
                handlePrint,
            } }),
        React.createElement(SidePanel, { component: Clues, props: { onSubmit: handleSubmitQuiz } })));
};
export { Crossword };
